/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import type { Service } from '@ahm/api-wrappers-core';
import {
  ActionIcon,
  Box,
  Group,
  Skeleton,
  rem,
  type ButtonProps,
} from '@mantine/core';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';
import { Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import TruckServiceItem from './truck-service-item';
import IconChevronLeft from '@/assets/icons/truck/chevron-left.svg';
import IconChevronRight from '@/assets/icons/truck/chevron-right.svg';
import { useHmService } from '@/features/booking/components/services/use-hm-service';
import { useTruckService } from '@/features/booking/components/services/use-truck-service';
import { getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = ButtonProps;
export default function TruckServiceSwiper(props: Props) {
  const {
    truckSelectedGroup,
    setSelectedService,
    selectedService,
    setIsSuggestPromotion,
  } = useBookingStore((state) => state);
  const pathname = usePathname();
  const { isHMService } = useMemo(() => getServiceType(pathname), [pathname]);

  const { isLoading, truckGroupedServices } = useTruckService();
  const { estimatedServices } = useHmService();

  const serviceList: Service[] = useMemo(() => {
    if (isHMService) return estimatedServices;
    return truckGroupedServices[truckSelectedGroup]?.services;
  }, [
    truckGroupedServices,
    truckSelectedGroup,
    estimatedServices,
    isHMService,
  ]);

  const [canSlidePrev, setCanSlidePrev] = useState(true);
  const [canSlideNext, setCanSlideNext] = useState(true);
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(
    null
  );

  const handleSelectVehicle = (service: Service, activeIndex: number) => {
    if (swiperInstance) {
      swiperInstance.slideTo(activeIndex);
      setIsSuggestPromotion(true);
      setSelectedService(service);
    } else {
      console.error('Swiper instance is not initialized');
    }
  };

  useEffect(() => {
    if (selectedService && swiperInstance && serviceList?.length) {
      const selectedIndex = serviceList.findIndex(
        (service) => service._id === selectedService._id
      );
      if (selectedIndex !== -1) {
        swiperInstance.slideTo(selectedIndex);
      }
    }
  }, [selectedService, serviceList]);

  return (
    <Box mt={rem(12)} {...props}>
      {isLoading ? (
        <Group
          gap={rem(8)}
          justify="flex-start"
          align="center"
          wrap="nowrap"
          style={{ overflow: 'hidden' }}
        >
          {[...Array(2)].map((_, index) => (
            <Skeleton
              key={index}
              height={rem(134)}
              radius={rem(8)}
              mb={rem(8)}
              w="40%"
            />
          ))}
        </Group>
      ) : (
        <Swiper
          onSwiper={(swiper: SwiperInstance) => setSwiperInstance(swiper)}
          style={{
            overflow: 'visible',
            height: 134,
            position: 'relative',
          }}
          spaceBetween={12}
          slidesPerView={2.7}
          mousewheel={true}
          modules={[Mousewheel]}
          onRealIndexChange={(index) => {
            setCanSlidePrev(!index.isBeginning);
            setCanSlideNext(!index.isEnd);
          }}
        >
          {serviceList?.length ? (
            <>
              {serviceList.map((item, idx) => {
                return (
                  <SwiperSlide key={item._id} style={{ width: '40%' }}>
                    <TruckServiceItem
                      service={item}
                      onSelect={() => handleSelectVehicle(item, idx)}
                    />
                  </SwiperSlide>
                );
              })}
            </>
          ) : null}
          <SwiperNavigation
            canSlidePrev={canSlidePrev}
            canSlideNext={canSlideNext}
          />
        </Swiper>
      )}
    </Box>
  );
}

type PropsSwiperNavigation = {
  canSlidePrev: boolean;
  canSlideNext: boolean;
};

const SwiperNavigation = ({
  canSlidePrev,
  canSlideNext,
}: PropsSwiperNavigation) => {
  const swiper = useSwiper();

  const handleSlidePrev = () => {
    swiper.slidePrev();
  };

  const handleSlideNext = () => {
    swiper.slideNext();
  };
  return (
    <>
      <Box
        pos="absolute"
        top="50%"
        left={0}
        style={{
          zIndex: 1,
          transform: 'translateY(-50%)',
          marginLeft: -8,
        }}
      >
        <ActionIcon
          size={36}
          variant="outline"
          bg="white"
          color="neutral"
          radius="xl"
          style={(theme) => ({
            border: 'none',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
            display: !canSlidePrev ? 'none' : 'flex',
            '&:hover': {
              color: theme.colors.neutral[9],
              backgroundColor: theme.white,
            },
          })}
          onClick={handleSlidePrev}
        >
          <IconChevronLeft size={18} />
        </ActionIcon>
      </Box>
      <Box
        pos="absolute"
        top="50%"
        right={0}
        style={{
          zIndex: 5,
          transform: 'translateY(-50%)',
          marginRight: -8,
        }}
      >
        <ActionIcon
          size={36}
          variant="outline"
          bg="white"
          color="neutral"
          radius="xl"
          style={(theme) => ({
            border: 'none',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
            display: !canSlideNext ? 'none' : 'flex',
            '&:hover': {
              color: theme.colors.neutral[9],
              backgroundColor: theme.white,
            },
          })}
          onClick={handleSlideNext}
        >
          <IconChevronRight size={18} />
        </ActionIcon>
      </Box>
    </>
  );
};
